import { useEffect } from 'react';

import { Pagination } from '../api/types';

// Keep an action queue on a page that exists (GRAVY-10323).
//
// The queues refresh live. An admin working page 2 while a colleague resolves
// records watches the result set shrink underneath them — and the client kept the
// obsolete page number, so the queue rendered an out-of-range page with no rows.
// Worse, `QueuePagination` hides itself when `total_pages <= 1`, so once the
// remainder fitted on one page there was no Previous control left and the admin
// was stranded on an empty page that read as "everything disappeared".
//
// The rule: after any response, a page beyond the last valid one moves to the
// last valid one. Page state is owned by the page component (`DashboardV2/index`),
// above the tab that unmounts, so the correction is reported upward through the
// same `onPageChange` the controls use rather than held here.
//
// ## Why this cannot loop
//
// The clamp only ever moves DOWNWARD, and only when `page > target`. After it
// fires, `page === target`, so the condition is false for the response that
// follows. It can therefore cost at most one extra fetch per shrink, never a
// cycle — which is the failure mode a naive "reset to 1 whenever the totals
// change" would risk.
//
// An empty queue settles on page 1 rather than page 0: `total_pages` is 0 with no
// results, and page 0 is not a page. The queue then renders its own empty state,
// which is the honest answer — as opposed to "no results on page 4".

const useQueuePageClamp = (
  pagination: Pagination | undefined,
  page: number,
  onPageChange: (next: number) => void,
) => {
  const totalPages = pagination?.total_pages;

  useEffect(() => {
    // No metadata yet (first load, or an API that predates GRAVY-10304): there
    // is nothing to clamp against, and guessing would be worse than waiting.
    if (totalPages == null) return;

    const lastValid = Math.max(1, totalPages);
    if (page > lastValid) onPageChange(lastValid);
  }, [totalPages, page, onPageChange]);
};

export default useQueuePageClamp;
